import { OG_IMAGE, SITE_URL, seoByPath } from '../../data/seo'
import { founder, openingHours, salon } from '../../data/content'
import { isOpen } from '../../core/rules/schedule'
import { policy } from '../../data/policies'
import type { Article } from '../../core/types/content'
import type { Product, Review } from '../../core/types/catalog'
import { isLowStock, isSoldOut } from '../../core/rules/stock'
import { absoluteUrl } from './seoText'

/**
 * Structured data (schema.org) voor Google. Puur: elke functie geeft een object terug,
 * `Seo.tsx` zet het in de pagina.
 */

const CONTEXT = 'https://schema.org'
const SALON_ID = `${SITE_URL}/#salon`
/** Zoveel reviews gaan mee in het productschema. */
const MAX_SCHEMA_REVIEWS = 5
const SCHEMA_DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'] as const
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/
/** Alleen een geldige barcode (8, 12, 13 of 14 cijfers); een verkeerde GTIN is erger dan geen. */
const VALID_GTIN = /^(\d{8}|\d{12,14})$/

/** Bedrijfsgegevens — altijd uit de actuele instellingen. */
export const businessSchema = () => ({
  '@context': CONTEXT,
  '@type': 'BeautySalon',
  '@id': SALON_ID,
  name: salon.name,
  description: seoByPath['/']?.description ?? '',
  url: SITE_URL,
  image: OG_IMAGE,
  email: salon.email,
  telephone: salon.phoneIntl,
  priceRange: '€€',
  address: {
    '@type': 'PostalAddress',
    streetAddress: salon.street,
    postalCode: salon.postalCode,
    addressLocality: salon.locality,
    addressCountry: 'NL',
  },
  hasMap: salon.routeUrl,
  openingHoursSpecification: openingHours.filter(isOpen).map((h) => ({
    '@type': 'OpeningHoursSpecification',
    dayOfWeek: SCHEMA_DAYS[h.weekday],
    opens: h.open,
    closes: h.close,
  })),
  founder: { '@type': 'Person', name: founder.name, jobTitle: 'Schoonheidsspecialist' },
  sameAs: [salon.instagram],
  knowsLanguage: ['nl', 'en'],
})

export function faqSchema(items: readonly { question: string; answer: string }[]) {
  return {
    '@context': CONTEXT,
    '@type': 'FAQPage',
    mainEntity: items.map((f) => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: { '@type': 'Answer', text: f.answer },
    })),
  }
}

export function breadcrumbSchema(items: readonly { name: string; path: string }[]) {
  return {
    '@context': CONTEXT,
    '@type': 'BreadcrumbList',
    itemListElement: items.map((it, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: it.name,
      item: `${SITE_URL}${it.path}`,
    })),
  }
}

export function articleSchema(article: Article, url: string) {
  return {
    '@context': CONTEXT,
    '@type': 'BlogPosting',
    headline: article.title,
    description: article.excerpt,
    image: article.image ? absoluteUrl(article.image) : OG_IMAGE,
    author: { '@type': 'Person', name: founder.name },
    publisher: { '@id': SALON_ID },
    mainEntityOfPage: url,
  }
}

const availabilityOf = (p: Product) =>
  isSoldOut(p) ? 'https://schema.org/OutOfStock' : isLowStock(p) ? 'https://schema.org/LimitedAvailability' : 'https://schema.org/InStock'

/** Product + Offer volgens Google's richtlijnen voor productfragmenten en Merchant listings. */
export function productSchema(product: Product, url: string, reviews: readonly Review[]) {
  const endOfNextYear = `${new Date().getFullYear() + 1}-12-31`
  const promoEnd = product.promo?.endsOn
  return {
    '@context': CONTEXT,
    '@type': 'Product',
    '@id': `${url}#product`,
    name: product.name,
    sku: product.wooId ? String(product.wooId) : product.id,
    ...(product.gtin && VALID_GTIN.test(product.gtin) ? { gtin: product.gtin } : {}),
    brand: { '@type': 'Brand', name: product.brand },
    image: [absoluteUrl(product.image)],
    description: product.why || product.note,
    category: product.category,
    // Google staat een AggregateRating zonder echte reviews niet toe.
    ...(product.reviews > 0
      ? {
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: product.rating,
            reviewCount: product.reviews,
            bestRating: 5,
            worstRating: 1,
          },
        }
      : {}),
    ...(reviews.length > 0
      ? {
          review: reviews.slice(0, MAX_SCHEMA_REVIEWS).map((r) => ({
            '@type': 'Review',
            author: { '@type': 'Person', name: r.name },
            reviewBody: r.body,
            ...(ISO_DATE.test(r.date.slice(0, 10)) ? { datePublished: r.date.slice(0, 10) } : {}),
            reviewRating: { '@type': 'Rating', ratingValue: r.rating, bestRating: 5, worstRating: 1 },
          })),
        }
      : {}),
    offers: {
      '@type': 'Offer',
      url,
      // Google verwacht de prijs als tekst met punt: "45.00".
      price: product.price.toFixed(2),
      priceCurrency: 'EUR',
      priceValidUntil: promoEnd && ISO_DATE.test(promoEnd) ? promoEnd : endOfNextYear,
      availability: availabilityOf(product),
      itemCondition: 'https://schema.org/NewCondition',
      seller: { '@id': SALON_ID },
      shippingDetails: {
        '@type': 'OfferShippingDetails',
        shippingRate: {
          '@type': 'MonetaryAmount',
          value: (product.price >= policy.freeShippingFrom ? 0 : policy.shippingCost).toFixed(2),
          currency: 'EUR',
        },
        shippingDestination: { '@type': 'DefinedRegion', addressCountry: 'NL' },
        deliveryTime: {
          '@type': 'ShippingDeliveryTime',
          handlingTime: { '@type': 'QuantitativeValue', minValue: 0, maxValue: 1, unitCode: 'DAY' },
          transitTime: { '@type': 'QuantitativeValue', minValue: 1, maxValue: 2, unitCode: 'DAY' },
        },
      },
      hasMerchantReturnPolicy: {
        '@type': 'MerchantReturnPolicy',
        applicableCountry: 'NL',
        returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
        merchantReturnDays: policy.returnDays,
        returnMethod: 'https://schema.org/ReturnByMail',
      },
    },
  }
}
